import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../config/firebase';

export interface GarageSubmission {
  // Section 1: Garage Owner Details
  fullName: string;
  contactEmail: string;
  phoneNumber: string;
  businessName: string;
  
  // Section 2: Garage Location & Identity
  garageName: string;
  fullAddress: string;
  googleMapsLink: string;
  garagePhotoUrl?: string;
  lotSignagePhotoUrl?: string;
  locationType: string;
  parkingType: string;
  
  // Section 3: Parking Capacity & Rates
  totalSpaces: number;
  spacesForParkRez: number;
  ratePerHour: number;
  dailyMaxRate: number;
  operatingHours: string;
  
  // Section 4: Security & Restrictions
  securityType: string;
  accessMethod: string;
  restrictions: string;
  specialInstructions: string;
  amenities: string;

  allowOvernightParking: boolean;
  
  // Section 5: Legal & Payout Info
  businessTaxId: string;
  payoutEmail: string;
  agreeToTerms: boolean;
  signature: string;
  signatureDate: string;
  
  // System fields
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: any;
  reviewedAt?: any;
  reviewedBy?: string;
  id?: string;
}

// Interface for approved garages that will be used in the main app
export interface ApprovedGarage {
  id: string;
  name: string;
  address: string;
  city: string;
  coordinates: { lat: number; lng: number };
  hourlyRate: number;
  dailyMaxRate: number;
  totalSpaces: number;
  availableSpots: number;
  amenities: string[];
  type: string;
  isActive: boolean;
  bookingRequired: boolean;
  photoUrl: string;
  partnerName: string;
  partnerContact: string;
  createdAt: string;
  updatedAt: string;
  // Additional fields from onboarding
  locationType: string;
  parkingType: string;
  operatingHours: string;
  securityType: string;
  accessMethod: string;
  restrictions: string;
  specialInstructions: string;
  allowOvernightParking: boolean;
  businessTaxId: string;
  payoutEmail: string;
  lotSignagePhotoUrl?: string;
  googleMapsLink?: string;
  // System fields
  zone: string;
  regionCode: string;
  popularityScore: number;
  reliabilityScore: number;
  totalReservations: number;
  successfulCheckIns: number;
  userRatings: {
    average: number;
    count: number;
  };
  trustSignals: {
    isVerifiedLot: boolean;
    isPartneredProvider: boolean;
    isMostBookedToday: boolean;
    isMostBookedThisWeek: boolean;
  };
  bookingActivity: {
    todayCount: number;
    thisWeekCount: number;
    lastWeekCount: number;
    totalBookings: number;
    lastUpdated: any;
  };
  partnerInfo: {
    name: string;
    verified: boolean;
    partnershipLevel: 'basic' | 'premium' | 'enterprise';
    partnershipDate: any;
  };
}

export const submitOnboardingForm = async (formData: any, garagePhoto?: File | null, lotSignagePhoto?: File | null) => {
  try {
    console.log('🔥 Starting onboarding form submission...');
    console.log('Form data received:', formData);
    console.log('Garage photo:', garagePhoto);
    console.log('Lot signage photo:', lotSignagePhoto);
    
    let garagePhotoUrl: string | undefined;
    let lotSignagePhotoUrl: string | undefined;

    // Upload garage photo if provided
    if (garagePhoto) {
      try {
        console.log('📸 Uploading garage photo...');
        const photoRef = ref(storage, `garage-photos/${Date.now()}-${garagePhoto.name}`);
        console.log('Photo reference created:', photoRef);
        const uploadResult = await uploadBytes(photoRef, garagePhoto);
        console.log('✅ Garage photo upload completed:', uploadResult);
        garagePhotoUrl = await getDownloadURL(uploadResult.ref);
        console.log('🔗 Garage photo URL:', garagePhotoUrl);
      } catch (photoError) {
        console.error('💥 Error uploading garage photo:', photoError);
        // Continue without the photo - don't fail the entire submission
        console.log('⚠️ Continuing without garage photo...');
      }
    }

    // Upload lot signage photo if provided
    if (lotSignagePhoto) {
      try {
        console.log('📸 Uploading lot signage photo...');
        const photoRef = ref(storage, `lot-signage-photos/${Date.now()}-${lotSignagePhoto.name}`);
        console.log('Signage photo reference created:', photoRef);
        const uploadResult = await uploadBytes(photoRef, lotSignagePhoto);
        console.log('✅ Lot signage photo upload completed:', uploadResult);
        lotSignagePhotoUrl = await getDownloadURL(uploadResult.ref);
        console.log('🔗 Lot signage photo URL:', lotSignagePhotoUrl);
      } catch (photoError) {
        console.error('💥 Error uploading lot signage photo:', photoError);
        // Continue without the photo - don't fail the entire submission
        console.log('⚠️ Continuing without lot signage photo...');
      }
    }

    // Remove File objects from formData before saving to Firestore
    const { garagePhoto: _, lotSignagePhoto: __, ...cleanFormData } = formData;

    // Build submission object, only including photo URLs if they exist
    const submission: any = {
      ...cleanFormData,
      status: 'pending',
      submittedAt: serverTimestamp()
    };

    // Only add photo URLs if they have values
    if (garagePhotoUrl) {
      submission.garagePhotoUrl = garagePhotoUrl;
    }
    if (lotSignagePhotoUrl) {
      submission.lotSignagePhotoUrl = lotSignagePhotoUrl;
    }

    console.log('💾 Preparing to save to Firestore...');
    console.log('Submission data:', submission);
    
    const docRef = await addDoc(collection(db, 'garageSubmissions'), submission);
    console.log('✅ Document saved to Firestore with ID:', docRef.id);
    
    const result = {
      success: true,
      id: docRef.id,
      garagePhotoUrl,
      lotSignagePhotoUrl,
      message: 'Success! Under review.'
    };
    
    console.log('🎉 Returning result:', result);
    return result;
  } catch (error) {
    console.error('💥 Error submitting onboarding form:', error);
    throw new Error('Failed to submit application. Please try again.');
  }
};

// Convert address to coordinates using Google Geocoding API
const getCoordinatesFromAddress = async (address: string): Promise<{ lat: number; lng: number } | null> => {
  try {
    // For now, return null - you can integrate Google Geocoding API here
    // const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${GOOGLE_MAPS_API_KEY}`);
    // const data = await response.json();
    // if (data.results && data.results[0]) {
    //   const location = data.results[0].geometry.location;
    //   return { lat: location.lat, lng: location.lng };
    // }
    console.log('📍 Geocoding not implemented yet, using default coordinates');
    return { lat: 40.7128, lng: -74.0060 }; // Default to NYC coordinates
  } catch (error) {
    console.error('❌ Error geocoding address:', error);
    return null;
  }
};

// Extract city and zone from address
const extractLocationInfo = (address: string): { city: string; zone: string; regionCode: string } => {
  // Simple extraction - you can enhance this with more sophisticated parsing
  const addressParts = address.split(',').map(part => part.trim());
  const city = addressParts[addressParts.length - 2] || 'Unknown City';
  const zone = addressParts[addressParts.length - 3] || 'Downtown';
  const regionCode = city === 'New York' ? 'NYC' : city === 'Washington' ? 'DC' : 'OTHER';
  
  return { city, zone, regionCode };
};

// Convert approved garage submission to paidParkingSpots format
export const approveGarageSubmission = async (submissionId: string, reviewedBy: string): Promise<{ success: boolean; garageId?: string; error?: string }> => {
  try {
    console.log('✅ Starting garage approval process...');
    
    // Get the submission
    const submissionDoc = await getDoc(doc(db, 'garageSubmissions', submissionId));
    if (!submissionDoc.exists()) {
      throw new Error('Garage submission not found');
    }
    
    const submission = submissionDoc.data() as GarageSubmission;
    console.log('📋 Processing submission:', submission.garageName);
    
    // Get coordinates from address
    const coordinates = await getCoordinatesFromAddress(submission.fullAddress);
    if (!coordinates) {
      throw new Error('Could not geocode address');
    }
    
    // Extract location info
    const locationInfo = extractLocationInfo(submission.fullAddress);
    
    // Convert to approved garage format
    const approvedGarage: any = {
      id: '', // Will be set by Firestore
      name: submission.garageName,
      address: submission.fullAddress,
      city: locationInfo.city,
      coordinates: coordinates,
      hourlyRate: submission.ratePerHour,
      dailyMaxRate: submission.dailyMaxRate || submission.ratePerHour * 24,
      totalSpaces: submission.spacesForParkRez,
      availableSpots: submission.spacesForParkRez,
      amenities: submission.amenities ? submission.amenities.split(',').map(a => a.trim()) : [],
      type: submission.locationType,
      isActive: true,
      bookingRequired: false,
      photoUrl: submission.garagePhotoUrl || '',
      partnerName: submission.businessName || submission.fullName,
      partnerContact: submission.contactEmail,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Additional fields from onboarding
      locationType: submission.locationType,
      parkingType: submission.parkingType,
      operatingHours: submission.operatingHours,
      securityType: submission.securityType || '',
      accessMethod: submission.accessMethod || '',
      restrictions: submission.restrictions || '',
      specialInstructions: submission.specialInstructions || '',
      allowOvernightParking: submission.allowOvernightParking,
      businessTaxId: submission.businessTaxId || '',
      payoutEmail: submission.payoutEmail,
      // Only add optional fields if they have values
      ...(submission.lotSignagePhotoUrl && { lotSignagePhotoUrl: submission.lotSignagePhotoUrl }),
      ...(submission.googleMapsLink && { googleMapsLink: submission.googleMapsLink }),
      // System fields
      zone: locationInfo.zone,
      regionCode: locationInfo.regionCode,
      popularityScore: 0,
      reliabilityScore: 0,
      totalReservations: 0,
      successfulCheckIns: 0,
      userRatings: {
        average: 0,
        count: 0,
      },
      trustSignals: {
        isVerifiedLot: false, // Removed - no longer showing this badge
        isPartneredProvider: false, // Removed - no longer showing this badge
        isMostBookedToday: false,
        isMostBookedThisWeek: false,
      },
      bookingActivity: {
        todayCount: 0,
        thisWeekCount: 0,
        lastWeekCount: 0,
        totalBookings: 0,
        lastUpdated: new Date(),
      },
      partnerInfo: {
        name: submission.businessName || submission.fullName,
        verified: true,
        partnershipLevel: 'basic',
        partnershipDate: new Date(),
      },
    };
    
    // Use a batch to ensure atomic operations
    const batch = writeBatch(db);
    
    // 1. Update submission status
    batch.update(doc(db, 'garageSubmissions', submissionId), {
      status: 'approved',
      reviewedAt: new Date(),
      reviewedBy: reviewedBy
    });
    
    // 2. Add to paidParkingSpots collection
    const garageRef = doc(collection(db, 'paidParkingSpots'));
    batch.set(garageRef, approvedGarage);
    
    // 3. Commit the batch
    await batch.commit();
    
    console.log('🎉 Garage approved and added to paidParkingSpots:', garageRef.id);
    
    return {
      success: true,
      garageId: garageRef.id
    };
    
  } catch (error) {
    console.error('❌ Error approving garage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Reject garage submission
export const rejectGarageSubmission = async (submissionId: string, reviewedBy: string, reason?: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('❌ Rejecting garage submission:', submissionId);
    
    await updateDoc(doc(db, 'garageSubmissions', submissionId), {
      status: 'rejected',
      reviewedAt: new Date(),
      reviewedBy: reviewedBy,
      rejectionReason: reason || 'Application rejected'
    });
    
    console.log('✅ Garage submission rejected');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error rejecting garage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Note: Delete functionality removed to maintain audit trail
// Use reject instead to keep records of all submissions

// Delete garage submission and corresponding approved garage
export const deleteGarageSubmission = async (submissionId: string, reviewedBy: string): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('🗑️ Deleting garage submission:', submissionId);
    
    // Get the submission first to check if it was approved
    const submissionDoc = await getDoc(doc(db, 'garageSubmissions', submissionId));
    if (!submissionDoc.exists()) {
      throw new Error('Garage submission not found');
    }
    
    const submission = submissionDoc.data() as GarageSubmission;
    
    // Use a batch to ensure atomic operations
    const batch = writeBatch(db);
    
    // 1. Delete the submission
    batch.delete(doc(db, 'garageSubmissions', submissionId));
    
    // 2. If it was approved, also delete from paidParkingSpots
    if (submission.status === 'approved') {
      // Find the corresponding garage in paidParkingSpots by matching key fields
      const q = query(
        collection(db, 'paidParkingSpots'),
        where('name', '==', submission.garageName),
        where('address', '==', submission.fullAddress),
        where('partnerContact', '==', submission.contactEmail)
      );
      
      const garageSnapshot = await getDocs(q);
      if (!garageSnapshot.empty) {
        // Delete the first matching garage (should only be one)
        batch.delete(garageSnapshot.docs[0].ref);
        console.log('🗑️ Also deleting approved garage from paidParkingSpots:', garageSnapshot.docs[0].id);
      }
    }
    
    // 3. Commit the batch
    await batch.commit();
    
    console.log('✅ Garage submission and related data deleted successfully');
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error deleting garage:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

// Get approved garages for the main app
export const getApprovedGarages = async (): Promise<ApprovedGarage[]> => {
  try {
    console.log('🔍 Fetching approved garages for main app...');
    
    const { collection, query, where, getDocs } = await import('firebase/firestore');
    const q = query(collection(db, 'paidParkingSpots'), where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    const approvedGarages = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ApprovedGarage[];
    
    console.log(`✅ Found ${approvedGarages.length} approved garages`);
    return approvedGarages;
    
  } catch (error) {
    console.error('❌ Error fetching approved garages:', error);
    return [];
  }
};

export const getGarageSubmission = async (id: string) => {
  try {
    // TODO: Implement get submission by ID
    // This would be used for checking application status
    return null;
  } catch (error) {
    console.error('Error fetching submission:', error);
    throw new Error('Failed to fetch application status.');
  }
}; 